<template>
  <section v-if="visible" class="prediction-history" :aria-busy="loading">
    <h3>本人的竞猜记录与赛果</h3>
    <p v-if="loading" role="status">正在查询原竞猜…</p>
    <ErrorNotice v-if="error" :message="error"></ErrorNotice>
    <article v-for="page in state?.rules?.pages || []" :key="page.key">
      <h4>{{ page.title }}</h4>
      <dl class="prediction-record-facts">
        <div><dt>{{ state.rules.predictionSeries ? '最近一次' : '我的选择' }}</dt><dd :class="{ 'is-pending': !progress(page).selectedKey }">{{ optionTitle(page, progress(page).selectedKey) }}</dd></div>
        <div v-if="state.rules.predictionSeries"><dt>参与次数</dt><dd>{{ progress(page).count }} 次；结果包含本玩法全部原票</dd></div>
        <div><dt>正式赛果</dt><dd :class="{ 'is-pending': !progress(page).winnerKey }">{{ optionTitle(page, progress(page).winnerKey, '尚未公布') }}</dd></div>
      </dl>
      <p v-if="progress(page).selectedKey && progress(page).winnerKey">{{ (state.rules.predictionSeries ? progress(page).completed : progress(page).selectedKey === progress(page).winnerKey) ? '已猜中' : '未猜中' }}<template v-if="state.rules.predictionEntry?.poolAmount || state.rules.predictionSeries && seriesPool(page).amount">；{{ progress(page).claimed ? Number(progress(page).currentAmount) > 0 ? '奖池份额已到账' : '零份额已确认，无现金账变' : state.rules.predictionSeries && !seriesPool(page).ready ? '等待本场全部原赛果' : '奖池结算以服务端为准' }}<template v-if="progress(page).completed && (!state.rules.predictionSeries || seriesPool(page).ready)"> {{ progress(page).currentAmount }} 元</template>。</template><template v-else>，本场不发奖励。</template></p>
    </article>
    <ActivityAction class="prediction-history-refresh" variant="text" :loading="loading" @click="refresh">重新查询竞猜结果</ActivityAction>
  </section>
</template>
<script setup>
import ErrorNotice from '../feedback/ErrorNotice.vue'
import { ref, watch, onBeforeUnmount } from 'vue';
import { gameplayApi } from '../../services/activities/webAdapter.js';
import { useWebActivity } from '../../services/activities/webAdapter.js';
import { predictionSeriesPool } from '../../services/activities/predictionSeries.mjs';
import { validGameState } from '../../services/activities/gameplay.mjs';
import ActivityAction from './ActivityAction.vue';
const { getAuth, getToken, storage } = useWebActivity();
const props=defineProps({activityId:{type:[Number,String],required:true},active:{type:Boolean,default:true},currentState:{type:Object,default:undefined},loadingState:{type:Boolean,default:false},loadError:{type:String,default:''}});
const emit=defineEmits(['loaded','refresh']);
const state=ref(null),visible=ref(false),loading=ref(false),error=ref('');
let sequence=0;
function seriesPool(page){return predictionSeriesPool(state.value,page.key);}
function progress(page){return state.value?.progress?.find(row=>row.key===page.key)||{};}
function optionTitle(page,key,empty='尚未参与'){return key?(page.options.find(option=>option.key===key)?.title||'原选项'):empty;}
async function refresh(){
 if(props.currentState!==undefined){emit('refresh');return;}
 const current=++sequence,id=props.activityId,token=getToken(),member=getAuth()?.memberId;
 state.value=null;error.value='';loading.value=false;
 if(!props.active||!token||!member){visible.value=false;return;}
 loading.value=true;
 try{
  const result=await gameplayApi.state(id);
  if(current!==sequence||!props.active||getToken()!==token||getAuth()?.memberId!==member)return;
  if(!validGameState(result,id,'PREDICTION')||!(result.rules.predictionEntry||result.rules.predictionSeries)){visible.value=false;return;}
  state.value=result;visible.value=true;emit('loaded');
 }catch(e){if(current===sequence&&getToken()===token&&getAuth()?.memberId===member&&visible.value)error.value=e?.message||'竞猜结果查询失败，请重试';}
 finally{if(current===sequence)loading.value=false;}
}
// 详情内共用父级已核验的同一份状态；独立历史恢复入口仍可自行只读查询。
function syncState(){
 sequence++;state.value=null;visible.value=false;error.value='';loading.value=false;
 if(props.currentState===undefined){void refresh();return;}
 if(!props.active||!getToken()||!getAuth()?.memberId)return;
 loading.value=props.loadingState;error.value=props.loadError;
 const result=props.currentState;
 if(validGameState(result,props.activityId,'PREDICTION')&&(result.rules.predictionEntry||result.rules.predictionSeries)){state.value=result;visible.value=true;}
 else visible.value=loading.value||Boolean(error.value);
}
watch(()=>[props.activityId,props.active,props.currentState,props.loadingState,props.loadError],syncState,{immediate:true});
function syncIdentity(event){if(!event.key||['kg_auth','kaiyun_web_token','kaiyun_web_user'].includes(event.key)){sequence++;state.value=null;visible.value=false;if(props.currentState===undefined)void refresh();}}
if(typeof window!=='undefined'){window.addEventListener('storage',syncIdentity);window.addEventListener('app:web-session-changed',syncIdentity);}
onBeforeUnmount(()=>{sequence++;if(typeof window!=='undefined'){window.removeEventListener('storage',syncIdentity);window.removeEventListener('app:web-session-changed',syncIdentity);}});
</script>
<style scoped>
.prediction-history { min-width: 0; margin: 12px 0 0; padding: 16px; border: 0; border-radius: 12px; color: var(--kg-text); background: var(--kg-canvas); font: 13px/1.75 var(--kg-font-ui); text-align: left; }
h3 { margin: 0 0 14px; color: var(--kg-text); font-size: 14px; font-weight: 600; line-height: 1.6; }
h4 { margin: 0 0 12px; color: var(--kg-text); font-size: 14px; font-weight: 500; line-height: 1.7; overflow-wrap: anywhere; }
p { margin: 8px 0; color: var(--promo-caption, #616783); font-size: 12px; overflow-wrap: anywhere; }
article + article { margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--kg-line); }
.prediction-record-facts { display: grid; gap: 10px; margin: 0; }
.prediction-record-facts > div { display: grid; grid-template-columns: 4em minmax(0, 1fr); align-items: baseline; gap: 16px; }
dt { color: var(--promo-caption, #616783); font-size: 12px; }
dd { min-width: 0; margin: 0; color: var(--kg-text); font-size: 13px; font-weight: 500; text-align: right; overflow-wrap: anywhere; }
dd.is-pending { color: var(--promo-caption, #616783); font-weight: 400; }
.prediction-history :deep(.prediction-history-refresh) { min-height: 44px; margin: 10px 0 -4px auto; padding: 4px 0; font-size: 12px; font-weight: 500; }
[role="alert"] { color: var(--kg-danger); }

.activity-text { display: block; }
img { object-fit: contain; }
</style>
